import Coloris from '@melloware/coloris';
import { useCallback, useEffect, useId, useRef } from 'react';

export const useInitializeColorPicker = ({
  inputRef,
  handleChange,
  style,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  handleChange: (color: string) => void;
  style: string;
}) => {
  const instanceId = useId();
  const instanceClass = `v-color-picker-${instanceId.replace(/:/g, '-')}`;

  const isDraggingRef = useRef(false);
  const pendingColorRef = useRef<string | null>(null);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;

    const colorPicker = document.querySelector('.clr-picker') as HTMLElement;
    if (!colorPicker) return;

    const isInsidePicker = colorPicker.contains(target);
    if (!isInsidePicker) return;

    const isInteractive =
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.closest('input, button');
    const isHueSlider = Boolean(target.closest('#clr-hue-slider'));

    if (!isInteractive || isHueSlider) {
      isDraggingRef.current = true;
      pendingColorRef.current = null;
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current && pendingColorRef.current !== null) {
      handleChange(pendingColorRef.current);
      pendingColorRef.current = null;
    }
    isDraggingRef.current = false;
  }, [handleChange]);

  useEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement) return;

    inputElement.classList.add(instanceClass);

    Coloris.setInstance(`.${instanceClass}`, {
      alpha: false,
      format: 'hex',
      formatToggle: true,
      themeMode: 'dark',

      onChange: color => {
        inputRef.current?.dispatchEvent(new Event('input'));

        if (isDraggingRef.current) pendingColorRef.current = color;
        else handleChange(color);
      },
    });

    Coloris({
      el: inputElement,
    });

    // NOTE: Coloris 호출 이후에 clr-field요소가 생성되므로 호출 이후에 해당 요소의 클래스를 변경
    const clrField = inputRef.current?.closest<HTMLDivElement>('.clr-field');
    if (clrField) {
      clrField.className = `clr-field ${style}`;
    }

    // NOTE: rxjs로 대체 하기 어려운 경우가 있어서 직접 이벤트 리스너를 추가
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mouseup', handleMouseUp, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      Coloris.close();
      inputElement.classList.remove(instanceClass);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceClass, style]);
};
